<template>
  <div
    class="min-h-screen flex flex-col justify-center items-center bg-[#007bff] md:bg-white px-4 py-10"
  >
    <div class="absolute top-5 left-5 md:hidden">
      <button @click="goBack">
        <i class="fas fa-arrow-left text-white md:text-[#007bff] text-lg" />
      </button>
    </div>
    <div class="absolute top-5 right-5">
      <button
        type="button"
        :class="[bookmarkButtonClasses, !canBookmark ? 'opacity-50 cursor-not-allowed' : '']"
        :aria-pressed="isBookmarked"
        :disabled="!canBookmark"
        :title="bookmarkButtonLabel"
        @click="toggleBookmark"
      >
        <i :class="[isBookmarked ? 'fas' : 'far', 'fa-bookmark']" />
        <span class="hidden sm:inline">{{ bookmarkButtonLabel }}</span>
      </button>
    </div>

    <img
      src="@/assets/quiz-finish.png"
      alt="Quiz Complete"
      class="w-48 md:w-72 mb-8"
    >

    <h2
      class="text-xl md:text-2xl font-semibold text-white md:text-[#007bff] mb-8"
    >
      Quizz Complete
    </h2>

    <div class="flex flex-row gap-4 mb-10 w-full max-w-md">
      <div
        class="flex-1 bg-white/20 backdrop-blur-sm rounded-xl py-4 flex flex-col items-center"
      >
        <div class="text-3xl mb-2">
          🌟
        </div>
        <div class="text-sm font-medium text-white md:text-[#007bff]">
          {{ grade }}
        </div>
      </div>
      <div
        class="flex-1 bg-white/20 backdrop-blur-sm rounded-xl py-4 flex flex-col items-center"
      >
        <div class="text-3xl mb-2">
          🥇
        </div>
        <div class="text-sm font-medium text-white md:text-[#007bff]">
          {{ scorePct }}%
        </div>
      </div>
      <div
        class="flex-1 bg-white/20 backdrop-blur-sm rounded-xl py-4 flex flex-col items-center"
      >
        <div class="text-3xl mb-2">
          ☀️
        </div>
        <div class="text-sm font-medium text-white md:text-[#007bff]">
          {{ expStr }} Points
        </div>
      </div>
    </div>

    <div class="flex justify-between gap-2 w-full">
      <button
        v-if="isSubscribed"
        class="w-full max-w-sm border border-white bg-[#007bff] text-white text-sm font-semibold px-8 py-3 rounded-full shadow-sm"
        @click="goReview"
      >
        Review Quiz
      </button>
      <button
        v-else
        class="w-full max-w-sm border border-[#F79009] bg-[#F79009] text-white text-sm font-semibold px-8 py-3 rounded-full shadow-sm"
        @click="goReview"
      >
        Review Quiz
      </button>
      <button
        class="w-full max-w-sm bg-white md:bg-[#007bff] md:text-white text-[#007bff] text-sm font-semibold px-8 py-3 rounded-full shadow-sm"
        @click="goHome"
      >
        Home
      </button>
    </div>

    <Transition name="modal">
      <div
        v-if="showUpgradeBanner"
        class="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      >
        <div
          class="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6 relative"
        >
          <!-- Modal Header -->
          <div class="flex justify-end items-center mb-2">
            <button
              class="p-2 hover:bg-gray-100 rounded-full"
              @click="showUpgradeBanner = false"
            >
              <i class="fas fa-times text-gray-500" />
            </button>
          </div>

          <!-- Modal Content it's time for upgrade-->
          <div class="text-center mb-4">
            <img
              src="@/assets/crown-uprage-pro.png"
              alt="crown"
              class="w-32 h-32 flex items-center justify-center mx-auto"
            >
            <h4 class="text-lg font-semibold text-gray-900 mb-1">
              It's time for Upgrade!
            </h4>
            <p class="text-gray-600 text-sm">
              Unlock quiz reviews and get full access to all quizzes
            </p>
          </div>

          <!-- Modal Actions -->
          <div class="space-y-3">
            <button
              class="w-full bg-blue-600 text-white py-3 rounded-full text-base font-semibold hover:bg-blue-700"
              @click="goSubscribe"
            >
              Subscribe Now
            </button>
            <button
              class="w-full border border-blue-600 text-blue-600 py-3 rounded-full text-base font-semibold hover:bg-blue-50"
              @click="skipSubscribePrompt"
            >
              Skip For Now
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="modal">
      <div
        v-if="showPinPrompt"
        class="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      >
        <div
          class="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6 relative"
        >
          <button
            class="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
            @click="closePinPrompt"
          >
            <i class="fas fa-times text-gray-500" />
          </button>

          <div class="text-center">
            <h4 class="text-lg font-semibold text-gray-900">
              Review PIN Required
            </h4>
            <p class="text-gray-600 text-sm mt-2">
              Enter the 4-digit PIN provided for this quiz to view the review.
            </p>
          </div>

          <div class="flex justify-center mt-6">
            <PinInput v-model="reviewPin" />
          </div>

          <p
            v-if="pinError"
            class="text-sm text-red-500 text-center mt-3"
          >
            {{ pinError }}
          </p>

          <div class="space-y-3 mt-6">
            <button
              class="w-full bg-blue-600 text-white py-3 rounded-full text-base font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="reviewPin.length !== 4 || isValidatingPin"
              @click="onSubmitPin"
            >
              <span
                v-if="isValidatingPin"
                class="flex items-center justify-center gap-2"
              >
                <i class="fas fa-spinner fa-spin" />
                Validating...
              </span>
              <span v-else>Continue to Review</span>
            </button>
            <button
              class="w-full border border-blue-600 text-blue-600 py-3 rounded-full text-base font-semibold hover:bg-blue-50"
              @click="closePinPrompt"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {
  CheckDailyLimitByStudent,
  VerifyQuizReviewPin,
} from "@/api/quizApi";
import { useAuthStore } from "@/stores/authStore";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import PinInput from "@/components/base/PinInput.vue";
import { useReviewBookmarkStore } from "@/stores/reviewBookmarkStore";

const router = useRouter();
const route = useRoute();
const isSubscribed = ref(false);
const showUpgradeBanner = ref(false);
const showPinPrompt = ref(false);
const reviewPin = ref("");
const pinError = ref("");
const isValidatingPin = ref(false);
const requireReviewPin = ref(
  ["1", "true", "yes"].includes(
    String(route.query.requireReviewPin ?? "").toLowerCase()
  )
);

const rawScore = Number(route.query.score ?? 0);
const rawExp = Number(route.query.exp ?? 0);
const grade = route.query.grade || "Try Again";
const startId = route.query.startId || 0;
const quizGuidRaw = route.query.quizGuid || route.query.guid || null;
const quizGuid = quizGuidRaw ? String(quizGuidRaw) : null;
const auth = useAuthStore();
const savedAuthUser = (() => {
  try {
    return JSON.parse(localStorage.getItem("auth_user") || "null");
  } catch {
    return null;
  }
})();

const studentId = computed(
  () =>
    auth?.userId ||
    auth?.user?.userId ||
    auth?.user?.data?.userId ||
    savedAuthUser?.data?.userId ||
    savedAuthUser?.userId ||
    null
);

const bookmarkStore = useReviewBookmarkStore();
bookmarkStore.hydrate();
const normalizedStartId = computed(() => {
  if (startId) return String(startId);
  const fromQuery = route.query.startId;
  return fromQuery ? String(fromQuery) : null;
});
const canBookmark = computed(() => Boolean(normalizedStartId.value));
const quizTitle = computed(() => {
  const rawTitle =
    route.query.quizTitle ||
    route.query.title ||
    route.query.quizName ||
    route.query.name;
  if (rawTitle && String(rawTitle).trim().length) {
    return String(rawTitle).trim();
  }
  return normalizedStartId.value
    ? `Quiz Review #${normalizedStartId.value}`
    : "Quiz Review";
});
const isBookmarked = computed(() =>
  canBookmark.value
    ? bookmarkStore.isBookmarked(normalizedStartId.value)
    : false
);
const bookmarkButtonLabel = computed(() =>
  isBookmarked.value ? "Bookmarked" : "Bookmark Review"
);
const bookmarkButtonClasses = computed(() => {
  const base =
    "flex items-center gap-2 px-3 py-2 rounded-full border text-sm font-semibold transition-colors";
  if (isBookmarked.value) {
    return [
      base,
      "bg-white text-[#007bff] border-white shadow-md md:bg-[#007bff]/10 md:border-[#007bff] md:text-[#007bff]",
    ].join(" ");
  }
  return [
    base,
    "bg-transparent text-white border-white hover:bg-white/10 md:border-[#007bff] md:text-[#007bff] md:bg-transparent md:hover:bg-[#007bff]/10",
  ].join(" ");
});

function buildBookmarkPayload() {
  if (!normalizedStartId.value) return null;
  const sanitizedQuery = {};
  const wrongOnlyRaw = route.query.wrongOnly;
  if (typeof wrongOnlyRaw !== "undefined") {
    sanitizedQuery.wrongOnly = wrongOnlyRaw;
  }
  if (requireReviewPin.value) {
    sanitizedQuery.requireReviewPin = "1";
  }
  if (quizGuid) {
    sanitizedQuery.quizGuid = quizGuid;
  }
  if (quizTitle.value) {
    sanitizedQuery.quizTitle = quizTitle.value;
  }

  return {
    startId: normalizedStartId.value,
    quizGuid,
    title: quizTitle.value,
    totalQuestions: null,
    correctCount: null,
    wrongCount: null,
    query: sanitizedQuery,
    requireReviewPin: requireReviewPin.value,
    key: null,
    route: {
      name: "QuizReviewPage",
      params: { startId: normalizedStartId.value },
      query: sanitizedQuery,
    },
  };
}

function toggleBookmark() {
  if (!canBookmark.value) return;
  const payload = buildBookmarkPayload();
  if (!payload) return;
  bookmarkStore.toggleBookmark(payload);
}

if (normalizedStartId.value && isBookmarked.value) {
  const payload = buildBookmarkPayload();
  if (payload) {
    bookmarkStore.updateBookmark(payload.startId, payload);
  }
}

async function checkDailyLimit() {
  try {
    if (!studentId.value) {
      showUpgradeBanner.value = false;
      return;
    }
    const data = await CheckDailyLimitByStudent(studentId.value);
    isSubscribed.value = data.isSubscribed;
  } catch (e) {
    console.warn("checkDailyLimit failed:", e?.message || e);
    showUpgradeBanner.value = false;
  }
}

const scorePct = computed(() =>
  Number.isFinite(rawScore)
    ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(
        rawScore
      )
    : "0"
);

const expStr = computed(() =>
  Number.isFinite(rawExp)
    ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(
        rawExp
      )
    : "0"
);

onMounted(async () => {
  checkDailyLimit();
});

function goSubscribe() {
  router.push("/subscribe");
}

function goHome() {
  router.push("/home");
}

function goToReviewPage(pin = "") {
  const query = { startId };
  if (quizGuid) {
    query.quizGuid = quizGuid;
  }
  if (quizTitle.value) {
    query.quizTitle = quizTitle.value;
  }
  if (requireReviewPin.value) {
    query.requireReviewPin = "1";
  }
  if (pin) {
    query.key = pin;
  }
  router.push({ path: `/quiz-review/${startId}`, query });
}

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.Message ||
  error?.message ||
  fallback;

const parseVerificationResponse = (payload) => {
  if (!payload) return {};
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch {
      return { raw: payload };
    }
  }
  return payload;
};

function openPinPrompt() {
  reviewPin.value = "";
  pinError.value = "";
  showPinPrompt.value = true;
}

function closePinPrompt() {
  showPinPrompt.value = false;
  pinError.value = "";
  reviewPin.value = "";
}

async function attemptReviewAccess(pin = "") {
  if (!startId) return;

  if (!requireReviewPin.value || !quizGuid) {
    goToReviewPage(pin);
    return;
  }

  isValidatingPin.value = true;
  pinError.value = "";

  try {
    const verification = parseVerificationResponse(
      await VerifyQuizReviewPin({
        guid: quizGuid,
        pin,
      })
    );

    if (typeof verification?.requiresPin === "boolean") {
      requireReviewPin.value = verification.requiresPin;
    }

    if (!requireReviewPin.value || verification?.isValid) {
      showPinPrompt.value = false;
      goToReviewPage(pin);
      return;
    }

    pinError.value =
      verification?.message || "Invalid PIN. Please try again.";
    showPinPrompt.value = true;
  } catch (error) {
    pinError.value = extractErrorMessage(
      error,
      "Unable to validate PIN. Please try again later."
    );
    showPinPrompt.value = true;
  } finally {
    isValidatingPin.value = false;
  }
}

async function handleReviewRequest(pin = "") {
  if (!startId) return;
  const sanitizedPin = (pin || "").replace(/\D/g, "").slice(0, 4);
  if (!sanitizedPin && requireReviewPin.value) {
    openPinPrompt();
    return;
  }
  await attemptReviewAccess(sanitizedPin);
}

function goReview() {
  if (!isSubscribed.value) {
    showUpgradeBanner.value = true;
    return;
  }
  handleReviewRequest();
}

function skipSubscribePrompt() {
  showUpgradeBanner.value = false;
  handleReviewRequest();
}

function goBack() {
  router.back();
}

function onSubmitPin() {
  const sanitized = (reviewPin.value || "").replace(/\D/g, "").slice(0, 4);
  reviewPin.value = sanitized;
  if (sanitized.length === 4 && !isValidatingPin.value) {
    handleReviewRequest(sanitized);
  }
}
</script>
